import React, { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactRequestsTab({ auth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/contact`);
      if (res.ok) setItems(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = {
    all: items.length,
    new: items.filter(i => (i.status || 'new') === 'new').length,
    contacted: items.filter(i => i.status === 'contacted').length,
    done: items.filter(i => i.status === 'done').length,
    archived: items.filter(i => i.status === 'archived').length,
  };

  const visible = items.filter(i => filter === 'all' || (i.status || 'new') === filter);

  const update = async (id, patch) => {
    try {
      const res = await fetch(`${API}/admin/contact/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) load();
    } catch { /* ignore */ }
  };

  const remove = async (id) => {
    if (!window.confirm('Slette denne henvendelsen permanent?')) return;
    try {
      const res = await fetch(`${API}/admin/contact/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${auth}` },
      });
      if (res.ok) load();
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="contact-requests-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-stone-900">Henvendelser fra nettsiden</h2>
        <button onClick={load} className="text-sm text-emerald-800 hover:text-emerald-900">Oppdater</button>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto">
        {[
          { id: 'new', label: 'Nye' },
          { id: 'contacted', label: 'Kontaktet' },
          { id: 'done', label: 'Ferdig' },
          { id: 'archived', label: 'Arkivert' },
          { id: 'all', label: 'Alle' },
        ].map(t => (
          <button key={t.id} onClick={() => setFilter(t.id)} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${filter === t.id ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} data-testid={`contact-filter-${t.id}`}>
            {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>

      {loading ? <p className="text-stone-500">Laster...</p> : visible.length === 0 ? (
        <div className="bg-white rounded-xl shadow border p-8 text-center text-stone-500">
          Ingen henvendelser {filter !== 'all' ? `med status "${filter}"` : 'ennå'}.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(req => {
            const status = req.status || 'new';
            const colors = {
              new: 'bg-amber-100 text-amber-800',
              contacted: 'bg-blue-100 text-blue-800',
              done: 'bg-emerald-100 text-emerald-800',
              archived: 'bg-stone-200 text-stone-600',
            };
            const isOpen = openId === req.id;
            return (
              <div key={req.id} className="bg-white rounded-xl shadow border border-stone-200 overflow-hidden" data-testid={`contact-card-${req.id}`}>
                <div className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>{
                          status === 'new' ? 'Ny' : status === 'contacted' ? 'Kontaktet' : status === 'done' ? 'Ferdig' : 'Arkivert'
                        }</span>
                        <span className="text-xs text-stone-500">{new Date(req.timestamp).toLocaleString('nb-NO')}</span>
                        {(req.images && req.images.length > 0) && (
                          <span className="text-xs text-stone-500">📎 {req.images.length} bilde(r)</span>
                        )}
                      </div>
                      <h3 className="font-semibold text-stone-900 text-lg">{req.name}</h3>
                      <p className="text-sm text-stone-600">
                        <a href={`mailto:${req.email}`} className="text-emerald-800 hover:underline">{req.email}</a>
                        {' · '}
                        <a href={`tel:${req.phone}`} className="text-emerald-800 hover:underline">{req.phone}</a>
                      </p>
                    </div>
                  </div>

                  <div className="bg-stone-50 border-l-4 border-stone-300 rounded-r p-3 text-sm text-stone-700 whitespace-pre-wrap" data-testid={`contact-message-${req.id}`}>
                    {req.message}
                  </div>

                  {req.images && req.images.length > 0 && (
                    <button onClick={() => setOpenId(isOpen ? null : req.id)} className="mt-2 text-sm text-emerald-800 hover:text-emerald-900" data-testid={`contact-toggle-images-${req.id}`}>
                      {isOpen ? 'Skjul bilder' : `Vis ${req.images.length} bilde(r)`}
                    </button>
                  )}
                  {isOpen && req.images && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {req.images.map((src, idx) => (
                        <a key={idx} href={src} target="_blank" rel="noreferrer" className="block bg-stone-100 rounded overflow-hidden">
                          <img src={src} alt={`Vedlegg ${idx+1}`} className="w-full h-32 object-cover hover:opacity-90" />
                        </a>
                      ))}
                    </div>
                  )}

                  {req.admin_note && (
                    <div className="mt-2 text-xs text-stone-600 italic">Notat: {req.admin_note}</div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-stone-100">
                    <a href={`mailto:${req.email}?subject=Re: Din henvendelse til Gravsteinoppfriskeren`} className="text-sm bg-emerald-800 text-white hover:bg-emerald-900 px-3 py-1.5 rounded-lg" data-testid={`contact-reply-${req.id}`}>
                      Svar på e-post
                    </a>
                    <a href={`tel:${req.phone}`} className="text-sm bg-stone-900 text-white hover:bg-stone-800 px-3 py-1.5 rounded-lg">
                      Ring
                    </a>
                    {status === 'new' && (
                      <button onClick={() => update(req.id, { status: 'contacted' })} className="text-sm bg-blue-100 text-blue-800 hover:bg-blue-200 px-3 py-1.5 rounded-lg" data-testid={`contact-mark-contacted-${req.id}`}>
                        Marker som kontaktet
                      </button>
                    )}
                    {status !== 'done' && (
                      <button onClick={() => update(req.id, { status: 'done' })} className="text-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-3 py-1.5 rounded-lg" data-testid={`contact-mark-done-${req.id}`}>
                        Marker som ferdig
                      </button>
                    )}
                    {status !== 'archived' && status !== 'new' && (
                      <button onClick={() => update(req.id, { status: 'archived' })} className="text-sm text-stone-600 hover:bg-stone-100 px-3 py-1.5 rounded-lg">
                        Arkiver
                      </button>
                    )}
                    {status === 'new' && (
                      <button onClick={() => update(req.id, { status: 'archived' })} className="text-sm text-stone-600 hover:bg-stone-100 px-3 py-1.5 rounded-lg">
                        Arkiver
                      </button>
                    )}
                    <button onClick={() => remove(req.id)} className="text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg ml-auto">
                      Slett
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
